/** County row in the who-were-reaching snapshot. */
export interface WhoWereReachingCountySnapshot {
  name: string;
  memberCount: number;
}

/** Snapshot JSON built from BigQuery demographic county metrics. */
export const WHO_WERE_REACHING_WAREHOUSE_SNAPSHOT_SOURCE = 'warehouse' as const;

/**
 * Coerces a raw county row member count to a finite number.
 * @param value - Warehouse or JSON value.
 * @returns Parsed count or 0.
 */
export const coerceWhoWereReachingMemberCount = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (value === null || value === undefined || value === '') {
    return 0;
  }
  const parsed = Number.parseInt(String(value), 10);
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Normalizes county rows from snapshot JSON (handles string counts from JSONB).
 * @param raw - `counties` array from snapshot.
 * @returns Valid county rows with numeric member counts.
 */
export const coerceWhoWereReachingCounties = (
  raw: unknown,
): WhoWereReachingCountySnapshot[] => {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw
    .map(row => {
      if (!row || typeof row !== 'object') {
        return null;
      }
      const record = row as { name?: unknown; memberCount?: unknown };
      const name = typeof record.name === 'string' ? record.name.trim() : '';
      const memberCount = coerceWhoWereReachingMemberCount(record.memberCount);
      if (!name || memberCount <= 0) {
        return null;
      }
      return { name, memberCount };
    })
    .filter((row): row is WhoWereReachingCountySnapshot => row !== null);
};

const MOCK_SEED_COUNTY_NAME_KEYS = new Set([
  'wayne',
  'oakland',
  'macomb',
  'genesee',
  'kent',
  'washtenaw',
]);

const countyNameKey = (name: string): string =>
  name.replace(/\s*County$/i, '').trim().toLowerCase();

/**
 * Detects seeded mock county rows (MI palette) shown on non-MI states in previews.
 * @param counties - County rows from a snapshot.
 * @param usStateCode - Customer or snapshot state code.
 * @returns Whether rows match the generator's fixed Michigan county set.
 */
export const isLikelyMockWhoWereReachingCounties = (
  counties: WhoWereReachingCountySnapshot[],
  usStateCode: string,
): boolean => {
  if (counties.length === 0) {
    return false;
  }
  const state = usStateCode.trim().toUpperCase();
  const keys = counties.map(row => countyNameKey(row.name));
  const matchesMockPalette =
    keys.length <= MOCK_SEED_COUNTY_NAME_KEYS.size &&
    keys.every(key => MOCK_SEED_COUNTY_NAME_KEYS.has(key));
  return matchesMockPalette && state !== 'MI';
};

/**
 * Returns true when the snapshot was built from warehouse demographic metrics.
 * @param dataSnapshot - Section snapshot JSON.
 * @returns Whether county counts should be treated as authoritative.
 */
export const isWarehouseWhoWereReachingSnapshot = (
  dataSnapshot: Record<string, unknown>,
): boolean =>
  dataSnapshot.snapshotSource === WHO_WERE_REACHING_WAREHOUSE_SNAPSHOT_SOURCE;
